import classNames from "classnames";
import React from "react";
import {
  ComboWithValue,
  ProgressBar,
  PuzzleButton,
  Container
} from "puzzle";
import { GridCellProps, GridColumn, GridRowClickEvent } from "@progress/kendo-react-grid";
import DropUpload from "../../../BatchUpload/DropUpload";
import { FILE } from "../../../BatchUpload/Types";
import _ from "lodash";
import { defaultCellRender } from "../../../../util/Util";
import { Keys } from "@progress/kendo-react-common";
import KendoGridAbstract from "../../../../util/KendoGridAbstract/KendoGridAbstract";
import DragAndDropPlaceholder from "../../../../UI/DragAndDrop/DragAndDropPlaceholder";
import DragAndDropWrapper from "../../../../UI/DragAndDrop/DragAndDropWrapper";

type SUBMISSION_LEVEL = {
  targetLanguage: string
  targetLanguageDisplayName: string
}

type PROPS = {
  moduleName: string,
  showDnDMsg: boolean,
  uploadActionName: string
}

type STATE = {
  allowUpload: boolean,
  editFileTicket: string | null,
  targetLanguages: any[]
}

class ReferenceFiles extends DropUpload<PROPS, STATE> {
  submissionLevel: SUBMISSION_LEVEL = {
    targetLanguage: 'SUBMISSION',
    targetLanguageDisplayName: 'Submission'
  };

  static defaultProps = {
    moduleName: 'DropUpload',
    uploadActionName: 'uploadFiles'
  };

  tooltip: any

  public state: any = {
    ...this.state, targetLanguages: [this.submissionLevel],
    editFileTicket: null
  };

  /**
   * @inheritDoc
   */
  createAlreadyUploadedData(data: any = [], forceAdd?: boolean): FILE[] {
    return data.map((item: any) => ({
      fileName: item.documentName || item.fileName,
      uploaded: forceAdd ? item.uploaded : DropUpload.UploadStatus.ALREADY_UPLOADED,
      documentId: item.documentId,
      targetId: item.targetId,
      repositoryTicket: item.repositoryTicket,
      targetLanguages: item.targetLanguageLocale ? item.targetLanguageLocale : this.submissionLevel.targetLanguage,
      inEdit: false,
      ...(forceAdd && { isAlreadyUpload: true })
    }));
  }

  /**
   * Creates drag and drop placeholder on instructions sections
   */
  createUploadPlaceholder = () => {
    return (
      <DragAndDropPlaceholder
        horizontal
        buttonText={this.translate('selectReferenceFilesBtn')}
        buttonOnClick={this.fileUploadHandler}
        dragOverHandler={this.dragOverHandler}
        buttonDisabled={!this.state.allowUpload}
        dNdPlaceholderTitle={this.translate('dragAndDropReferenceFiles')}
      />
    );
  };

  /**
   * Grid cell render
   * @param defaultRendering
   * @param cellProps
   */
  cellRender = (defaultRendering: React.ReactElement<HTMLTableCellElement> | null, cellProps: GridCellProps): JSX.Element => {
    const { dataItem, field } = cellProps;
    if (defaultRendering && field) {
      let children;
      let editedProps = {
        ...defaultRendering.props
      };

      switch (cellProps.field) {
        case 'fileName':
          editedProps.className += '';
          children = defaultCellRender(this.translate, () => null, cellProps);
          break;
        case 'targetLanguages':
          const inEdit = dataItem.inEdit;
          if (inEdit) {
            editedProps.className += ' tp-file-name-in-edit';
          }
          children = this.targetLanguageRenderer(cellProps);
          break;
        case 'fileStatus':
          editedProps.className += ' tp-delete-cell';
          children = this.statusRenderer(cellProps);
          break;
        case 'action':
          editedProps.className += '';
          children = this.deleteRowRenderer(cellProps);
          break;
        default:
          children = dataItem[field];
          break;
      }

      return React.cloneElement(defaultRendering, editedProps, children);
    }
    return <></>
  };


  /**
   * @inheritDoc
   */
  createUploadedFilesList = () => {
    const { droppedFiles, dragging, editFileTicket } = this.state;
    let gridItems = droppedFiles.map((item: FILE) => {

      if (item.uploaded !== DropUpload.UploadStatus.ALREADY_UPLOADED) {
        // @ts-ignore
        item.inEdit = item.repositoryTicket === editFileTicket;
      }
      return item;
    });
    const classes = classNames("tp-uploaded-files-grid", {
      "tp-masked": dragging
    });
    return (
      <KendoGridAbstract
        captionText={this.translate('SRL-referenceFilesGridCaptionText')}
        rowHeight={32}
        className={classes}
        data={gridItems}
        editField={'inEdit'}
        cellRender={this.cellRender}
        onRowClick={this.gridRowClick}
      >
        <GridColumn
          field="fileName"
          editable={false}
          title={this.translate('fileName')}
        />
        <GridColumn
          field="targetLanguages"
          title={this.translate('level')}
        />
        <GridColumn
          field="fileStatus"
          editable={false}
          title={this.translate('fileStatus')}
        />
        <GridColumn
          field="action"
          className={'k-text-center'}
          headerClassName={'k-text-center'}
          editable={false}
          title={this.translate('Action')}
        />
      </KendoGridAbstract>
    );
  };

  createUploadStatusSection = () => {
    const { droppedFiles } = this.state;
    const statusStyle = classNames(
      'tp-files-uploaded-info-bar',
      {
        'tp-display-none': !droppedFiles.length
      });

    return (
      <div className={statusStyle}>
        {!!droppedFiles.length &&
          (
            <div className='k-hbox k-flex-grow'>
              <PuzzleButton
                iconClass={'tp-icon tp-add-circle'}
                fillMode={'flat'}
                size={'small'}
                themeColor={'primary'}
                onClick={this.fileUploadHandler}
              >
                {this.translate('addMoreFiles')}
              </PuzzleButton>
            </div>
          )
        }
        {/*<div className={'tp-files-counter'}>*/}
        {/*  <div className="tp-icon tp-icon-sm tp-check" />*/}
        {/*  {this.translate('filesFound', { numberOfFiles: uploaded })}*/}
        {/*</div>*/}
        {/*<div className="tp-files-counter">*/}
        {/*  <div className="tp-icon tp-icon-sm tp-x" />*/}
        {/*  {this.translate('filesWithIssues', { numberOfFiles: failed })}*/}
        {/*</div>*/}
      </div>
    );
  };

  /**
   * Enter in edit mode
   */
  enterEdit = (dataItem: any) => {
    if (dataItem.uploaded !== DropUpload.UploadStatus.ALREADY_UPLOADED) {
      this.setState({
        editFileTicket: dataItem.repositoryTicket
      });
    }
  };

  /**
   * Close edit mode
   */
  exitEdit = () => {
    this.setState({
      editFileTicket: null
    });
  };

  /**
   * Map method for dropped and new files for update
   */
  findFilesForUpdate = (uploadFiles: any[], droppedFile: any) => {
    let findUploadedFile = uploadFiles.find((file: any) => {
      return !droppedFile.repositoryTicket && droppedFile.fileName === file.name;
    });

    if (findUploadedFile) {
      droppedFile = {
        fileName: findUploadedFile.name,
        uploaded: DropUpload.UploadStatus.UPLOADED,
        targetLanguages: this.submissionLevel.targetLanguage
      }
    }

    return droppedFile;
  };

  /**
   * @inheritDoc
   */
  getSubmissionIdForUpload = () => {
    return null;
  }

  /**
   * Row click handler. Start inline editing
   */
  gridRowClick = (event: GridRowClickEvent) => {
    let dataItem = event.dataItem;
    this.enterEdit(dataItem);
  };

  /**
   * Item change handler. Set selected target level to file
   */
  itemChange = (cellProps: GridCellProps, event: any) => {
    let value = event.target.value;

    if (value) {
      let dataItem = cellProps.dataItem;
      let field = cellProps.field;

      let droppedFiles = _.cloneDeep(this.state.droppedFiles);
      let droppedLength = droppedFiles.length;
      let current;
      while (droppedLength) {
        droppedLength--;
        current = droppedFiles[droppedLength];
        if (current.repositoryTicket === dataItem.repositoryTicket) {
          if (field) {
            droppedFiles[droppedLength][field] = value;
            break;
          }
        }
      }

      this.setState({
        droppedFiles: droppedFiles
      });
    }
  };

  /**
   * status row cell renderer
   */
  statusRenderer = (cellProps: GridCellProps) => {
    const { progresses = {} } = this.state;
    let { moduleId } = this.props;
    return (
      <>
        <div className={'tp-progress-cell-inner-wrap'}>
          <ProgressBar
            key={`ProgressBar_${moduleId}_${cellProps.dataItem.fileName}`}
            className={'tp-uploaded-list-item-status k-text-ellipsis'}
            file={cellProps.dataItem}
            fileName={cellProps.dataItem.fileName}
            group={moduleId}
            progresses={progresses}
          />
        </div>
      </>
    )
  };

  /**
   * Delete button row cell renderer
   */
  deleteRowRenderer = (cellProps: GridCellProps) => {
    const { dataItem } = cellProps;

    return (
      <PuzzleButton
        aria-label={this.translate('SRL-deleteFile', { fileName: dataItem.fileName })}
        titleText={this.translate('deleteFile')}
        onClick={this.removeFile.bind(this, [cellProps.dataItem])}
        iconClass={'tp-icon tp-delete'}
        fillMode={'flat'}
        themeColor={'primary'}
        size={'small'}
        tooltipPosition={'right'}
        withTooltip
      />
    );
  }

  /**
   * Reset selection for target languages
   */
  resetTargetLanguagesSelection = () => {
    let { droppedFiles, targetLanguages } = this.state;
    const deletedFiles: FILE[] = [];
    droppedFiles.forEach((item: any) => {
      let langItem = _.find(targetLanguages, { 'targetLanguage': item.targetLanguages });
      if (!langItem) {
        deletedFiles.push(item);
      }
    });
    this.setState({
      droppedFiles: [...droppedFiles]
    }, () => {
      if (deletedFiles.length) {
        this.removeFile(deletedFiles);
      }
    });
  };

  /**
   * @inheritDoc
   */
  setExistingFiles = (files: any, details: any, languages?: any[], forceAdd?: boolean): void => {
    let findLanguages = languages && details.targetLanguage ? languages.filter((item: any) => {
      return details.targetLanguage.includes(item.locale);
    }) : [];

    let parsedLanguages = findLanguages.map((item: any) => {
      return {
        targetLanguage: item.locale,
        targetLanguageDisplayName: item.value
      };
    });

    this.setTargetLanguages(parsedLanguages);
    if (forceAdd) {
      files = files?.map((item: any) => {
        return {
          ...item,
          targetLanguageLocale: _.find(parsedLanguages, (lang) => item.targetLanguages === lang.targetLanguage)?.targetLanguage
        }
      });
    }

    const { project } = details;
    const existingFiles = files ? this.createAlreadyUploadedData(files, forceAdd) : [];
    this.setState({
      allowUpload: !!project,
      projectId: project,
      droppedFiles: existingFiles,
      dragging: false,
      uploaded: existingFiles.length,
    });
  };

  /**
   * Set available target languages
   */
  setTargetLanguages = (targetLanguages: any[]) => {
    let editedTargetLanguages = [...targetLanguages];
    editedTargetLanguages.splice(0, 0, this.submissionLevel);
    // @ts-ignore
    this.setState({
      targetLanguages: editedTargetLanguages
    }, this.resetTargetLanguagesSelection);
  };

  /**
   * Target language cell renderer. Render dropDown for editMode
   */
  targetLanguageRenderer = (cellProps: GridCellProps) => {
    let dataItem = cellProps.dataItem;
    let field = cellProps.field;
    let value = field ? dataItem[field] : '';

    let displayValue;
    if (!dataItem.inEdit) {
      let findItem = this.state.targetLanguages.find((item: any) => item.targetLanguage === value);
      if (findItem) {
        displayValue = findItem.targetLanguageDisplayName;
      }
    }

    return (
      <>
        {dataItem.inEdit ? (
          <span onKeyDown={this.targetLanguageComboKeyHandler.bind(this)}>
            <ComboWithValue
              clearButton={false}
              data={this.state.targetLanguages}
              defaultValue={value}
              onBlur={this.exitEdit}
              onClose={this.itemChange.bind(this, cellProps)}
              tabIndex={1}
              size={'small'}
              textField={'targetLanguageDisplayName'}
              valueField={'targetLanguage'}
              ref={(combo: any) => {
                if (combo) {
                  combo.component._input.focus();
                }
              }}
            />
          </span>
        ) : (
          <span
            className={'tp-selected-file-name'}
            tabIndex={0}
            onKeyDown={(event) => {
              const { keyCode } = event;
              if (keyCode === Keys.enter) {
                this.enterEdit(dataItem)
              }
            }}
            onClick={() => {
              this.enterEdit(dataItem)
            }}
          >
            {displayValue}
          </span>
        )}
      </>
    );
  };

  /**
   * Exit edit mode if key ESC is pressed
   */
  targetLanguageComboKeyHandler = (event: any) => {
    const { keyCode } = event;
    if (keyCode === Keys.esc) {
      this.exitEdit();
    }
  };

  renderBody() {
    const { showUploadStatus = true, showDnDMsg } = this.props;
    const inputId = [this.moduleName, this.moduleId, "ReferenceFiles"].join("_")
    return (
      <Container
        className="tp-drag-and-drop-wrapper"
        flex1
      >
        <div
          role={'alert'}
          className={'sr-only accessibilityAnnouncements'}
        />
        <input
          type="file"
          ref={this.filesInput}
          className="tp-file-input"
          multiple
          id={inputId}
          onChange={this.fileSelectHandler}
        />
        <label htmlFor={inputId} className="sr-only">{this.translate("SRL-uploadLabel")}</label>
        <div className={`tp-drag-and-drop ${this.state.droppedFiles.length !== 0 ? 'pd-has-files' : ''}`}
             onDragEnter={this.dragOverHandler}>
          {this.state.droppedFiles.length === 0 ? this.createUploadPlaceholder() : this.createUploadedFilesList()}
          <DragAndDropWrapper
            show={this.state.dragging}
            dNdMessage={showDnDMsg && this.translate('referenceFilesDnDMessage')}
            className={'tp-reference-section'}
            dragLeaveHandler={this.dragLeaveHandler}
            onDropHandler={this.onDropHandler}
            dragOverHandler={this.maskedDragOverHandler}
          />
        </div>
        {showUploadStatus && this.createUploadStatusSection()}
      </Container>
    );
  }

}

export default ReferenceFiles;
